import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Send,
  Email,
  Visibility,
  Refresh,
  FilterList,
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
} from '@mui/icons-material';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import {
  EmailLog,
  EmailTemplate,
  EmailStats,
  EmailStatus,
  SendEmailRequest,
  SendBulkEmailRequest,
  SendEventEmailRequest,
} from '../../types/email';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`email-tabpanel-${index}`}
      aria-labelledby={`email-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const AdminEmailsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [emails, setEmails] = useState<EmailLog[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [emailDetailsOpen, setEmailDetailsOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailLog | null>(null);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    template_name: '',
    status: '',
    recipient_email: '',
  });
  const [formData, setFormData] = useState<SendEmailRequest>({
    recipient_email: '',
    recipient_name: '',
    template_name: '',
    subject: '',
    language: 'en',
    context: {},
  });
  const [eventFormData, setEventFormData] = useState<SendEventEmailRequest>({
    event_type: 'happy_new_year',
    template_name: '',
    subject: '',
    language: '',
    context: {},
  });
  const { user } = useAuth();

  useEffect(() => {
    if (user?.is_admin) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [emailsData, templatesData, statsData] = await Promise.all([
        apiService.getEmailLogs(filters),
        apiService.getEmailTemplates(),
        apiService.getEmailStats('all_time'),
      ]);
      setEmails(emailsData.emails);
      setTemplates(templatesData);
      setStats(statsData);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load email data');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!formData.recipient_email || !formData.template_name) {
      setError('Please fill in recipient email and template');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await apiService.sendEmail(formData);
      setSuccess('Email sent successfully!');
      setSendDialogOpen(false);
      resetForm();
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEventEmail = async () => {
    try {
      setLoading(true);
      setError('');
      await apiService.sendEventEmail(eventFormData);
      setSuccess('Event emails sent successfully!');
      setEventDialogOpen(false);
      resetEventForm();
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send event emails');
    } finally {
      setLoading(false);
    }
  };

  const handleViewEmail = async (emailId: number) => {
    try {
      const email = await apiService.getEmailLog(emailId);
      setSelectedEmail(email);
      setEmailDetailsOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load email details');
    }
  };

  const resetForm = () => {
    setFormData({
      recipient_email: '',
      recipient_name: '',
      template_name: '',
      subject: '',
      language: 'en',
      context: {},
    });
  };

  const resetEventForm = () => {
    setEventFormData({
      event_type: 'happy_new_year',
      template_name: '',
      subject: '',
      language: '',
      context: {},
    });
  };

  const getStatusChip = (status: EmailStatus) => {
    switch (status) {
      case EmailStatus.SENT:
        return <Chip icon={<CheckCircle />} label="Sent" color="success" size="small" />;
      case EmailStatus.FAILED:
        return <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />;
      case EmailStatus.PENDING:
        return <Chip icon={<Schedule />} label="Pending" color="warning" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  if (loading && emails.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          Email Management
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadData}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Send />}
            onClick={() => setSendDialogOpen(true)}
          >
            Send Email
          </Button>
          <Button
            variant="contained"
            startIcon={<Email />}
            onClick={() => setEventDialogOpen(true)}
            color="secondary"
          >
            Send Event Email
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Card sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' } }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Sent
              </Typography>
              <Typography variant="h4">{stats.total_sent}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' } }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Failed
              </Typography>
              <Typography variant="h4" color="error">
                {stats.total_failed}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' } }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Success Rate
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.success_rate.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' } }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Templates Used
              </Typography>
              <Typography variant="h4">
                {Object.keys(stats.emails_by_template).length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Email List */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Email History
            </Typography>
            <Stack direction="row" spacing={2}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Template</InputLabel>
                <Select
                  value={filters.template_name}
                  label="Template"
                  onChange={(e) => setFilters({ ...filters, template_name: e.target.value })}
                >
                  <MenuItem value="">All Templates</MenuItem>
                  {templates.map((template) => (
                    <MenuItem key={template.name} value={template.name}>
                      {template.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Recipient</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Template</TableCell>
                  <TableCell>Language</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="textSecondary">No emails found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  emails.map((email) => (
                    <TableRow key={email.id} hover>
                      <TableCell>
                        {new Date(email.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>{email.recipient_email}</TableCell>
                      <TableCell>{email.subject}</TableCell>
                      <TableCell>
                        <Chip label={email.template_name} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{email.language.toUpperCase()}</TableCell>
                      <TableCell>{getStatusChip(email.status)}</TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewEmail(email.id)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Send Email Dialog */}
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Send Email</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Recipient Email"
              type="email"
              fullWidth
              required
              value={formData.recipient_email}
              onChange={(e) => setFormData({ ...formData, recipient_email: e.target.value })}
            />
            <TextField
              label="Recipient Name (Optional)"
              fullWidth
              value={formData.recipient_name}
              onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
            />
            <FormControl fullWidth required>
              <InputLabel>Template</InputLabel>
              <Select
                value={formData.template_name}
                label="Template"
                onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
              >
                {templates.map((template) => (
                  <MenuItem key={template.name} value={template.name}>
                    {template.display_name} - {template.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Subject (Optional - auto-generated if not provided)"
              fullWidth
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Language</InputLabel>
              <Select
                value={formData.language}
                label="Language"
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="fr">French</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSendEmail} variant="contained" disabled={loading}>
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Event Email Dialog */}
      <Dialog open={eventDialogOpen} onClose={() => setEventDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Send Event Email</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Event Type</InputLabel>
              <Select
                value={eventFormData.event_type}
                label="Event Type"
                onChange={(e) =>
                  setEventFormData({
                    ...eventFormData,
                    event_type: e.target.value as SendEventEmailRequest['event_type'],
                  })
                }
              >
                <MenuItem value="happy_new_year">Happy New Year</MenuItem>
                <MenuItem value="tournament_announcement">Tournament Announcement</MenuItem>
                <MenuItem value="match_reminder">Match Reminder</MenuItem>
                <MenuItem value="tournament_reminder">Tournament Reminder</MenuItem>
                <MenuItem value="club_newsletter">Club Newsletter</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Subject (Optional - auto-generated if not provided)"
              fullWidth
              value={eventFormData.subject}
              onChange={(e) => setEventFormData({ ...eventFormData, subject: e.target.value })}
            />
            <Alert severity="info">
              Event emails will be sent to all active users. You can optionally specify recipient IDs to send to specific users only.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSendEventEmail} variant="contained" disabled={loading} color="secondary">
            Send to All Users
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Details Dialog */}
      <Dialog open={emailDetailsOpen} onClose={() => setEmailDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Email Details</DialogTitle>
        <DialogContent>
          {selectedEmail && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Recipient
              </Typography>
              <Typography>{selectedEmail.recipient_email}</Typography>
              <Divider />
              <Typography variant="subtitle2" color="textSecondary">
                Subject
              </Typography>
              <Typography>{selectedEmail.subject}</Typography>
              <Divider />
              <Typography variant="subtitle2" color="textSecondary">
                Template
              </Typography>
              <Typography>{selectedEmail.template_name}</Typography>
              <Divider />
              <Typography variant="subtitle2" color="textSecondary">
                Status
              </Typography>
              {getStatusChip(selectedEmail.status)}
              {selectedEmail.error_message && (
                <>
                  <Divider />
                  <Typography variant="subtitle2" color="textSecondary">
                    Error Message
                  </Typography>
                  <Alert severity="error">{selectedEmail.error_message}</Alert>
                </>
              )}
              {selectedEmail.context && (
                <>
                  <Divider />
                  <Typography variant="subtitle2" color="textSecondary">
                    Context
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                      {JSON.stringify(selectedEmail.context, null, 2)}
                    </pre>
                  </Paper>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

